import { doc, setDoc } from 'firebase/firestore';

import {
  cloneMovementConfig,
  normalizeMovementConfig,
  toLegacyWaypointPath,
} from '../../lib/npcMovement';
import {
  clampTilePoint,
  normalizeCameraZoom,
  normalizeWorldLayout,
  type WorldLayout,
} from '../../game/worldLayout';
import { normalizeWorldAnnotations } from '../../lib/worldAnnotations';
import { getLevelDesign } from '../levelDesigns';
import type { LevelDesignDocument } from '../../types/levelEditor';
import type { SceneDocument, SceneNpcPlacement } from '../../types/scene';
import { getFirebaseDb } from './client';
import { persistSceneAssetsForSave } from './sceneSaveAssetPersistence';
import type { SceneCompositionInput } from './scenes';
import { renderSceneAssetsFromLevelDesign } from '../../ui/pages/sceneComposerLevelDesignAssets';

const SCENES_COLLECTION = 'scenes';
export type SceneReconciliationMode = 'read-only' | 'persist';

const resolveSceneLayoutFromDesign = (
  design: Pick<LevelDesignDocument, 'canvasWidth' | 'canvasHeight'>,
): WorldLayout =>
  normalizeWorldLayout({
    tileWidth: design.canvasWidth,
    tileHeight: design.canvasHeight,
  });

const normalizeSpawnTile = (
  point: { x: number; y: number } | undefined,
  layout: WorldLayout,
  fallback: { x: number; y: number },
): { x: number; y: number } => clampTilePoint(point ?? fallback, layout);

const normalizePlacementToLayout = (
  placement: SceneNpcPlacement,
  layout: WorldLayout,
): SceneNpcPlacement => {
  const waypointPath = Array.isArray(placement.waypointPath)
    ? placement.waypointPath.map((entry) => clampTilePoint(entry, layout))
    : [];
  const movementConfig = placement.movementConfig
    ? cloneMovementConfig(
        normalizeMovementConfig(placement.movementConfig, waypointPath, layout),
        layout,
      )
    : undefined;
  return {
    ...placement,
    spawnX: clampTilePoint({ x: placement.spawnX, y: placement.spawnY }, layout).x,
    spawnY: clampTilePoint({ x: placement.spawnX, y: placement.spawnY }, layout).y,
    movementConfig,
    waypointPath: movementConfig
      ? toLegacyWaypointPath(movementConfig, waypointPath)
      : waypointPath,
  };
};

const normalizePlacementsToLayout = (
  placements: SceneNpcPlacement[],
  layout: WorldLayout,
): SceneNpcPlacement[] =>
  placements.map((placement) => normalizePlacementToLayout(placement, layout));

const normalizeAnnotationsToLayout = (
  annotations: SceneDocument['annotations'],
  layout: WorldLayout,
): SceneDocument['annotations'] => normalizeWorldAnnotations(annotations, layout);

const hasSameWorldLayout = (left: WorldLayout, right: WorldLayout): boolean =>
  left.tileWidth === right.tileWidth && left.tileHeight === right.tileHeight;

const hasSamePoint = (left: { x: number; y: number }, right: { x: number; y: number }): boolean =>
  left.x === right.x && left.y === right.y;

const hasSamePlacements = (left: SceneNpcPlacement[], right: SceneNpcPlacement[]): boolean =>
  JSON.stringify(left) === JSON.stringify(right);

const hasSameAnnotations = (
  left: SceneDocument['annotations'],
  right: SceneDocument['annotations'],
): boolean => JSON.stringify(left) === JSON.stringify(right);

const shouldRenderReadOnlyDerivedAssets = (
  scene: SceneDocument,
  layoutChanged: boolean,
): boolean => {
  if (scene.status === 'draft' || scene.status === 'archived') {
    return true;
  }

  if (layoutChanged) {
    return true;
  }

  return (
    !scene.backgroundUrl?.trim() || !scene.traversalMaskUrl?.trim() || !scene.thumbnailUrl?.trim()
  );
};

const persistReconciledScene = async (
  sceneId: string,
  scene: Pick<
    SceneDocument,
    | 'animatedGifs'
    | 'backgroundUrl'
    | 'foregroundUrl'
    | 'defaultCameraZoom'
    | 'levelDesignId'
    | 'npcPlacements'
    | 'annotations'
    | 'ownerId'
    | 'playerSpawnTile'
    | 'status'
    | 'thumbnailUrl'
    | 'traversalMaskUrl'
    | 'worldLayout'
  >,
): Promise<
  Pick<
    SceneDocument,
    'animatedGifs' | 'backgroundUrl' | 'foregroundUrl' | 'thumbnailUrl' | 'traversalMaskUrl'
  >
> => {
  const persistedAssets = await persistSceneAssetsForSave({
    input: scene,
    ownerId: scene.ownerId,
    sceneId,
  });

  await setDoc(
    doc(getFirebaseDb(), SCENES_COLLECTION, sceneId),
    {
      ...persistedAssets,
      defaultCameraZoom: scene.defaultCameraZoom,
      annotations: scene.annotations,
      npcPlacements: scene.npcPlacements,
      playerSpawnTile: scene.playerSpawnTile,
      worldLayout: scene.worldLayout,
    },
    { merge: true },
  );

  return persistedAssets;
};

const loadSceneDesign = async (
  levelDesignId: string,
  initialDesign?: LevelDesignDocument | null,
): Promise<LevelDesignDocument | null> => {
  if (!levelDesignId.trim()) {
    return null;
  }
  if (initialDesign !== undefined) {
    return initialDesign;
  }
  return getLevelDesign(levelDesignId);
};

export const resolveSceneLayoutFromLevelDesignId = async (
  levelDesignId: string,
  initialDesign?: LevelDesignDocument | null,
): Promise<WorldLayout | null> => {
  const design = await loadSceneDesign(levelDesignId, initialDesign);
  return design ? resolveSceneLayoutFromDesign(design) : null;
};

export const reconcileSceneCompositionInputWithLevelDesign = async (
  input: SceneCompositionInput,
  initialDesign?: LevelDesignDocument | null,
): Promise<SceneCompositionInput> => {
  const design = await loadSceneDesign(input.levelDesignId, initialDesign);
  if (!design) {
    return {
      ...input,
      defaultCameraZoom: normalizeCameraZoom(input.defaultCameraZoom),
      playerSpawnTile: clampTilePoint(
        input.playerSpawnTile,
        normalizeWorldLayout(input.worldLayout),
      ),
    };
  }

  const rendered = await renderSceneAssetsFromLevelDesign(design);
  return {
    ...input,
    animatedGifs: rendered.animatedGifs,
    backgroundUrl: rendered.backgroundUrl,
    foregroundUrl: rendered.foregroundUrl,
    defaultCameraZoom: normalizeCameraZoom(input.defaultCameraZoom),
    annotations: normalizeAnnotationsToLayout(
      design.annotations ?? input.annotations,
      rendered.worldLayout,
    ),
    npcPlacements: normalizePlacementsToLayout(input.npcPlacements, rendered.worldLayout),
    playerSpawnTile: normalizeSpawnTile(
      input.playerSpawnTile,
      rendered.worldLayout,
      rendered.playerSpawnTile,
    ),
    thumbnailUrl: rendered.thumbnailUrl,
    traversalMaskUrl: rendered.traversalMaskUrl,
    worldLayout: rendered.worldLayout,
  };
};

export const reconcileSceneDocumentWithLevelDesign = async (
  scene: SceneDocument,
  options: {
    design?: LevelDesignDocument | null;
    mode?: SceneReconciliationMode;
  } = {},
): Promise<SceneDocument> => {
  const design = await loadSceneDesign(scene.levelDesignId, options.design);
  if (!design) {
    return scene;
  }
  const mode = options.mode ?? 'read-only';

  const canonicalLayout = resolveSceneLayoutFromDesign(design);
  const normalizedSpawnTile = normalizeSpawnTile(scene.playerSpawnTile, canonicalLayout, {
    x: Math.floor(canonicalLayout.tileWidth / 2),
    y: Math.floor(canonicalLayout.tileHeight / 2),
  });
  const normalizedAnnotations = normalizeAnnotationsToLayout(
    design.annotations ?? scene.annotations,
    canonicalLayout,
  );
  const normalizedPlacements = normalizePlacementsToLayout(scene.npcPlacements, canonicalLayout);
  const normalizedZoom = normalizeCameraZoom(scene.defaultCameraZoom);

  const layoutChanged = !hasSameWorldLayout(scene.worldLayout, canonicalLayout);
  const spawnChanged = !hasSamePoint(scene.playerSpawnTile, normalizedSpawnTile);
  const annotationsChanged = !hasSameAnnotations(scene.annotations, normalizedAnnotations);
  const placementsChanged = !hasSamePlacements(scene.npcPlacements, normalizedPlacements);
  const zoomChanged = scene.defaultCameraZoom !== normalizedZoom;
  const shouldRenderDerivedAssets =
    mode === 'persist' || shouldRenderReadOnlyDerivedAssets(scene, layoutChanged);

  if (
    !shouldRenderDerivedAssets &&
    !spawnChanged &&
    !annotationsChanged &&
    !placementsChanged &&
    !zoomChanged &&
    !layoutChanged
  ) {
    return scene;
  }

  const rendered = shouldRenderDerivedAssets
    ? await renderSceneAssetsFromLevelDesign(design)
    : null;
  const reconciledScene: SceneDocument = {
    ...scene,
    animatedGifs: rendered?.animatedGifs ?? scene.animatedGifs,
    backgroundUrl: rendered?.backgroundUrl ?? scene.backgroundUrl,
    foregroundUrl: rendered?.foregroundUrl ?? scene.foregroundUrl,
    defaultCameraZoom: normalizedZoom,
    annotations: normalizedAnnotations,
    npcPlacements: normalizedPlacements,
    playerSpawnTile: normalizedSpawnTile,
    thumbnailUrl: rendered?.thumbnailUrl ?? scene.thumbnailUrl,
    traversalMaskUrl: rendered?.traversalMaskUrl ?? scene.traversalMaskUrl,
    worldLayout: rendered?.worldLayout ?? canonicalLayout,
  };

  if (mode !== 'persist') {
    return reconciledScene;
  }

  const persistedAssets = await persistReconciledScene(scene.id, {
    ...reconciledScene,
  });
  return {
    ...reconciledScene,
    ...persistedAssets,
  };
};
